import { RouteLocationNormalized, LocationQueryRaw } from "vue-router";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import { getHistoryState } from "./history.utils";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { Store } from "vuex";
import { store } from "@/shared-store";
import { IVuexState } from "@/apps/merchant3/shared-models/IVuex";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";
import AuthenticationService from "@/apps/merchant3/services/merchant.login.service";
import { OriginationChannelEnum } from "@/apps/merchant3/enums/originationChannelEnum";
import { getCountryCode } from "@/apps/merchant3/helpers/country";
import i18n from "@/i18n";

export const navigationGuardForCheckOut = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): INavigationGuardType | boolean => {
  const { historyHasBack, reloadingCurrentPage } = getHistoryState();
  if (
    (historyHasBack && reloadingCurrentPage) ||
    process.env.NODE_ENV !== "production"
  ) {
    return true;
  }

  const toName = to.name;
  const fromFormatted = from?.name || null;
  if (!fromFormatted) {
    logEvent(EventLogs.Events.UNAUTHORIZED_PAGE, {
      details: "The Clerk is probably trying to move forward using the URL",
      to: toName as string,
      from: fromFormatted as string,
    });
    logAction(ActionLogs.Events.UNAUTHORIZED_PAGE, {
      details: "The Clerk is probably trying to move forward using the URL",
      to: toName,
      from: fromFormatted,
    });
    return {
      name: RouteName.M3TermsAndConditions,
      params: { countryCode: getCountryCode() },
    };
  }

  return true;
};

export const navigationGuardForClerk = (): INavigationGuardType | boolean => {
  if (!isMerchantLoggedIn()) {
    return redirectToLogin();
  } else if (isClerkSelected()) {
    return {
      name: RouteName.M3Welcome,
      params: { countryCode: getCountryCode() },
    };
  } else {
    return true;
  }
};

export const navigationGuardForDirectToCustomerWelcome = async (
  to: RouteLocationNormalized
): Promise<INavigationGuardType | boolean> => {
  if (to.name !== RouteName.DirectToCustomerWelcome) {
    return false;
  }

  // Check if welcome=1 is in query params - if so, show welcome page
  const showWelcomeScreen = to.query.welcome === "1";
  const countryCode = (to.params.countryCode as string)?.toUpperCase();

  // If D2C session is already set, check if country changed
  if (isDirectToCustomerSessionSet()) {
    const storedCountry = sessionStorage.getItem(
      SessionStorageEnum.MerchantCountry
    );

    // If country changed, clear session to re-authenticate with new country
    if (storedCountry && storedCountry !== countryCode) {
      sessionStorage.clear();
    } else {
      if (showWelcomeScreen) {
        logEvent(EventLogs.Events.DIRECT_TO_CUSTOMER_WELCOME_PAGE_VISITED, {
          countryCode,
          queryParams: to.query,
        });
        return true;
      }
      logEvent(EventLogs.Events.DIRECT_TO_CUSTOMER_WELCOME_PAGE_SKIPPED, {
        countryCode,
        queryParams: to.query,
      });
      return {
        name: RouteName.DirectToCustomerTermsAndConditions,
        params: { countryCode: countryCode.toLowerCase() },
        query: to.query,
      };
    }
  }

  // If coming from M3 we should clear previous session values
  sessionStorage.clear();
  // Basic Country ISO validation
  if (!countryCode || !/^[A-Z]{2}$/i.test(countryCode)) {
    return false;
  }

  try {
    const authenticationService = new AuthenticationService();
    const data = await authenticationService.directToCustomerLogin(countryCode);

    sessionStorage.setItem(SessionStorageEnum.ClerkJwt, data.clerkToken);
    sessionStorage.setItem(SessionStorageEnum.Clerk, data.clerkId.toString());
    sessionStorage.setItem(SessionStorageEnum.MerchantJwt, data.merchantToken);
    sessionStorage.setItem(
      SessionStorageEnum.Merchant,
      data.merchantId.toString()
    );
    sessionStorage.setItem(
      SessionStorageEnum.MerchantCountry,
      data.merchantCountry
    );
    sessionStorage.setItem(
      SessionStorageEnum.OriginationChannel,
      OriginationChannelEnum.DirectToCustomer
    );
    localStorage.setItem("lang", data.language);
    i18n.global.locale.value = data.language;

    // If welcome=1, show welcome page instead of auto-redirecting
    if (showWelcomeScreen) {
      logEvent(EventLogs.Events.DIRECT_TO_CUSTOMER_WELCOME_PAGE_VISITED, {
        countryCode,
        queryParams: to.query,
      });
      return true;
    }

    // Auto-redirect to terms page with UTM query params preserved
    logEvent(EventLogs.Events.DIRECT_TO_CUSTOMER_WELCOME_PAGE_SKIPPED, {
      countryCode,
      queryParams: to.query,
    });
    return {
      name: RouteName.DirectToCustomerTermsAndConditions,
      params: { countryCode: countryCode.toLowerCase() },
      query: to.query,
    };
  } catch (error) {
    // TODO: add redirect to "not supported" page (TBD)
    return false;
  }
};

export const isDirectToCustomerSessionSet = (): boolean => {
  const originationChannel =
    sessionStorage.getItem(SessionStorageEnum.OriginationChannel) ?? null;
  return (
    isClerkSelected() &&
    originationChannel === OriginationChannelEnum.DirectToCustomer
  );
};

export const navigationGuardForMerchant = (
  to: RouteLocationNormalized
): INavigationGuardType | boolean => {
  const countryCode = getCountryCode();
  if (to.name == RouteName.M3MerchantLogin && isMerchantLoggedIn()) {
    sessionStorage.removeItem(SessionStorageEnum.MerchantJwt);
    return { name: RouteName.M3MerchantLogin };
  }
  if (isMerchantLoggedIn()) {
    return { name: RouteName.M3ClerkLogin, params: { countryCode } };
  } else {
    return true;
  }
};

export const isMerchantLoggedIn = (): boolean => {
  const vuexStore = store as unknown as Store<IVuexState>;

  if (sessionStorage.getItem(SessionStorageEnum.MerchantJwt)) {
    vuexStore.dispatch(
      "merchant/setMerchantJwt",
      sessionStorage.getItem(SessionStorageEnum.MerchantJwt)
    );
    return true;
  } else {
    return false;
  }
};

export const isClerkSelected = (): boolean => {
  return !!sessionStorage.getItem(SessionStorageEnum.ClerkJwt);
};

export const checkDeviceTag = (): boolean => {
  return !!sessionStorage.getItem(SessionStorageEnum.DeviceTag);
};
export const checkCartInitiated = (): boolean => {
  return !!sessionStorage.getItem(SessionStorageEnum.CartTag);
};

export const redirectToLogin = (): INavigationGuardType => {
  const ecommerceSaleFinished = !!sessionStorage.getItem(
    SessionStorageEnum.EcommerceSaleFinished
  );
  const countryCode = getCountryCode();

  // Check for E-commerce flow, since we clear all the session storage after the sale is completed
  if (ecommerceSaleFinished) {
    return {
      name: RouteName.M3EcommerceFinishedSale,
      params: { countryCode },
    };
  }

  return {
    name: RouteName.M3MerchantLogin,
  };
};

export interface INavigationGuardType {
  name?: string;
  params?: { countryCode?: string };
  query?: LocationQueryRaw;
}

export const navigationGuard = (): INavigationGuardType | boolean => {
  const countryCode = getCountryCode();
  if (!isMerchantLoggedIn()) {
    return redirectToLogin();
  } else if (!isClerkSelected()) {
    return { name: RouteName.M3MerchantLogin };
  } else if (!checkCartInitiated()) {
    return { name: RouteName.M3Welcome, params: { countryCode } };
  } else {
    return true;
  }
};

export const navigationGuardForDirectToCustomer = (
  to: RouteLocationNormalized
):
  | INavigationGuardType
  | boolean => {
  // D2C flow: cart is created on the terms page, not on the welcome page
  // So we only check if D2C session is set, not if cart is initiated
  if (!isDirectToCustomerSessionSet()) {
    const countryCode = (to.params.countryCode as string)?.toLowerCase();

    return {
      name: RouteName.DirectToCustomerWelcome,
      params: { countryCode: countryCode ?? getCountryCode() },
    };
  } else {
    return true;
  }
};

export const navigationGuardWelcome = async (): Promise<
  INavigationGuardType | boolean
> => {
  if (!isMerchantLoggedIn()) {
    return redirectToLogin();
  } else if (!isClerkSelected()) {
    return { name: RouteName.M3MerchantLogin };
  } else {
    return true;
  }
};

export const navigationGuardInStorePayment = ():
  | INavigationGuardType
  | boolean => {
  const countryCode = getCountryCode();
  if (!isMerchantLoggedIn()) {
    return redirectToLogin();
  } else if (!isClerkSelected()) {
    return { name: RouteName.M3MerchantLogin };
  } else if (!checkDeviceTag()) {
    return { name: RouteName.M3Welcome, params: { countryCode } };
  } else {
    return true;
  }
};
